import { useEffect, useRef, useState } from 'react';
import { ChevronUp, ListChecks, Truck, CheckCheck, Flame, ClipboardList } from 'lucide-react';
import useAktivniDavka from './hooks/useAktivniDavka';
import useVTeple from './hooks/useVTeple';
import DavkaPanel from './DavkaPanel';
import ChecklistPanel from './ChecklistPanel';
import KOdvozu from './KOdvozu';
import PrehledRozvozu, { usePrehledRozvozu } from './PrehledRozvozu';

/**
 * Pracovní plocha Mojí směny — to, co se zrovna dělá.
 *
 * Dávka a checklist se dřív skládaly **pod sebe** a braly si plochu navzájem:
 * rozvozci s dávkou začínal checklist až hluboko pod přehybem a poslední
 * položky mizely pod plovoucí lištou. Přitom se v jednu chvíli dělá vždycky
 * jen jedna z těch dvou věcí.
 *
 * Proto je tu segment: **co se zrovna dělá, dostane celou plochu; to druhé se
 * srazí na jeden proužek s počtem**. Ťuknutí do proužku je prohodí. Kdo dávku
 * nemá (obsluha), nevidí proužek žádný a checklist zabere plochu celou.
 *
 * Výchozí rozbalený segment je dávka — když jede rozvoz, je to to hlavní.
 * Rozvozce bez dávky má na jejím místě „K odvozu" (hotové objednávky
 * k výběru, Cockpit 2.0) — taky práce, která má přednost před kontrolami.
 * Jakmile si člověk přepne ručně, drží se jeho volba (dokud dávka nezmizí).
 *
 * **Plochy dlaždic.** Klepnutí na dlaždici Auto nebo Pokladna v hlavičce
 * otevře v ploše „Já za volantem" nebo „Moje pokladna" (`plocha` + `obsahPlochy`).
 * Dávka i kontroly se pak srazí do proužků u **spodní hrany** — neposouvají se
 * podle toho, kolik je nad nimi obsahu, takže je palec najde vždycky na stejném
 * místě. Klepnutí na proužek plochu zavře a otevře ten segment.
 *
 * Plocha dlaždice se zavře i sama, když ji nikdo nepoužívá — člověk se na sebe
 * podívá a nemusí nic zavírat:
 *  - po `NAVRAT_PO_MS` bez dotyku (každý dotyk nebo posun odpočet vynuluje),
 *  - hned, když se něco stane: nová dávka nebo změna jejího stavu, jiná fáze,
 *  - po návratu do aplikace, když byla schovaná aspoň `NAVRAT_PO_SKRYTI_MS`.
 * Bez odpočtu na obrazovce — návrat je tichý.
 */

/** Po jak dlouhé nečinnosti se plocha dlaždice vrátí k práci. */
const NAVRAT_PO_MS = 45000;
/** Kdo telefon zamkne na minutu a víc, po návratu najde rovnou práci. */
const NAVRAT_PO_SKRYTI_MS = 60000;
export default function PracovniPlocha({
    faze,                      // aktuální fáze směny (nese checklisty)
    onSaveOdpoved,
    onOtevritFazi,
    onOpenCockpit,
    davkaZapnuta = true,       // ve fázi ukončení už rozvoz neběží
    zamestnanecId = null,      // přihlášený zaměstnanec — osobní kanál rozvozce (realtime dávky)
    plocha = null,             // otevřená plocha dlaždice ('ridic' | 'pokladna') nebo null
    obsahPlochy = null,        // její obsah (ZaVolantemPanel / MojePokladnaPanel)
    onZavritPlochu,            // zavři plochu dlaždice — vrátí se dávka / kontroly
    pozastavitNavrat = false,  // nad plochou je otevřený sheet (účtenka, vklad…) — odpočet stojí
    isDark,
    fill = false,
}) {
    const { davka, zastavky, nadchazejici, refetch } = useAktivniDavka({ enabled: davkaZapnuta, zamestnanecId });
    const [volba, setVolba] = useState(null);

    const maDavku = davkaZapnuta && !!davka;

    // Rozvozce bez dávky: hotové objednávky si vybere rovnou tady (K odvozu),
    // cockpit se otevře až na trase.
    const teplo = useVTeple({ enabled: davkaZapnuta && !davka });
    const maOdvoz = !maDavku && davkaZapnuta && teplo.naSmene && teplo.pocet > 0;
    const maChecklist = !!faze && (faze.checklisty?.length > 0);

    // Přehled všech rozvozů v systému — jen rozvozci (server jiným vrátí 403 → null)
    const prehled = usePrehledRozvozu({ enabled: davkaZapnuta });
    const [prehledOtevren, setPrehledOtevren] = useState(false);
    const maPrehled = davkaZapnuta && prehled.objednavky !== null;
    const prouzekPrehled = maPrehled && (
        <Prouzek Icon={ClipboardList} label="Rozvozy v systému" doplnek={String(prehled.objednavky.length)} barva="#2563EB" onClick={() => setPrehledOtevren(true)} isDark={isDark} />
    );
    const modalPrehled = prehledOtevren && (
        <PrehledRozvozu objednavky={prehled.objednavky} onObnovit={prehled.nacti} onClose={() => setPrehledOtevren(false)} isDark={isDark} />
    );
    const prouzekOdvoz = (onClick) => (
        <Prouzek Icon={Flame} label="K odvozu" doplnek={`${teplo.pocet} hotové`} barva="#FF8A00" onClick={onClick} isDark={isDark} />
    );
    const odvoz = (
        <KOdvozu
            hotove={teplo.hotove} variSe={teplo.variSe} doporuceno={teplo.doporuceno} limit={teplo.limit}
            onVzato={() => { teplo.obnovit(); refetch?.(); onOpenCockpit?.(); }}
        />
    );

    // ── Návrat plochy dlaždice ──────────────────────────────────────────
    // Callback přes ref: rodič ho vytváří při každém renderu a periodické
    // obnovy dat renderují často — v závislostech by odpočet nikdy nedoběhl.
    const zavritRef = useRef(onZavritPlochu);
    zavritRef.current = onZavritPlochu;
    const plochaRef = useRef(null);

    useEffect(() => {
        // Nad plochou je otevřený sheet: člověk pracuje, jen ne přímo v ploše.
        // Odpočet stojí a po zavření sheetu běží znovu od začátku — jinak by se
        // po vyfocení účtenky vrátil místo do pokladny k dávce.
        if (!plocha || pozastavitNavrat) return undefined;
        const zavrit = () => zavritRef.current?.();
        let casovac = setTimeout(zavrit, NAVRAT_PO_MS);
        const znovu = () => { clearTimeout(casovac); casovac = setTimeout(zavrit, NAVRAT_PO_MS); };
        // Scroll nebublá — capture ho chytí i ze zanořených seznamů.
        const udalosti = ['pointerdown', 'touchstart', 'wheel', 'scroll', 'keydown'];
        const el = plochaRef.current;
        udalosti.forEach(u => el?.addEventListener(u, znovu, { passive: true, capture: true }));
        let schovanoOd = null;
        const viditelnost = () => {
            if (document.hidden) { schovanoOd = Date.now(); return; }
            if (schovanoOd && Date.now() - schovanoOd >= NAVRAT_PO_SKRYTI_MS) zavrit();
            else znovu();
            schovanoOd = null;
        };
        document.addEventListener('visibilitychange', viditelnost);
        return () => {
            clearTimeout(casovac);
            udalosti.forEach(u => el?.removeEventListener(u, znovu, { capture: true }));
            document.removeEventListener('visibilitychange', viditelnost);
        };
    }, [plocha, pozastavitNavrat]);

    // Práce má přednost: nová dávka, změna jejího stavu nebo jiná fáze plochu
    // dlaždice zavře hned. První render se nepočítá — to není změna.
    const klicPrace = `${davka?.id ?? '-'}:${davka?.stav ?? '-'}:${faze?.id ?? '-'}`;
    const predchoziPrace = useRef(klicPrace);
    useEffect(() => {
        if (predchoziPrace.current !== klicPrace && plocha) zavritRef.current?.();
        predchoziPrace.current = klicPrace;
    }, [klicPrace, plocha]);

    if (plocha && obsahPlochy) {
        const otevri = (segment) => { setVolba(segment); zavritRef.current?.(); };
        return (
            <>
                {/* Spodek vybledne jako u kontrol — obsah zajíždí pod proužky
                    plynule, ne useknutý o hranu. */}
                <div ref={plochaRef} style={{
                    flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    WebkitMaskImage: 'linear-gradient(to bottom, #000 calc(100% - 22px), transparent 100%)',
                    maskImage: 'linear-gradient(to bottom, #000 calc(100% - 22px), transparent 100%)',
                }}>
                    {obsahPlochy}
                </div>
                {(maDavku || maOdvoz || maChecklist || maPrehled) && (
                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {prouzekPrehled}
                        {maOdvoz && prouzekOdvoz(() => otevri('odvoz'))}
                        {maDavku && (
                            <Prouzek
                                Icon={Truck}
                                label={`Dávka ${davka.kod ?? '#' + davka.id}`}
                                doplnek={STAV_DAVKY[davka.stav] || null}
                                barva="#975ACD"
                                onClick={() => otevri('davka')}
                                isDark={isDark}
                            />
                        )}
                        {maChecklist && (
                            <Prouzek
                                Icon={ListChecks}
                                label={faze.checklisty[0]?.nazev || 'Kontroly'}
                                doplnek={pocetKontrol(faze)}
                                barva="#10B981"
                                onClick={() => otevri('checklist')}
                                isDark={isDark}
                            />
                        )}
                    </div>
                )}
                {modalPrehled}
            </>
        );
    }

    // Ruční volba přežije jen dokud dává smysl — po dojetí dávky by jinak
    // zůstala rozbalená prázdná plocha.
    const prace = maDavku ? 'davka' : maOdvoz ? 'odvoz' : null;
    const aktivni = ((volba === 'davka' || volba === 'odvoz') && volba !== prace) ? (prace ?? 'checklist')
        : (volba === 'checklist' && !maChecklist) ? prace
        : (volba ?? prace ?? 'checklist');

    // Fáze bez checklistu je běžná — třeba „Průběh" v rozvozových šablonách
    // nese jen neplacenou pauzu. Prázdný návrat by ale nechal tlačítko
    // vyskočit nahoru pod pruh a zbytek obrazovky prázdný; plocha si proto
    // bere svou výšku i tehdy, když v ní nic není.
    if (!maDavku && !maChecklist) {
        if (maOdvoz) return <>{odvoz}{prouzekPrehled}{modalPrehled}</>;
        return (
            <>
            <div style={{
                flex: 1, minHeight: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '0 24px', textAlign: 'center',
            }}>
                <CheckCheck size={26} style={{ color: '#10B981', opacity: 0.75 }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Nic k odškrtnutí
                </div>
                <div style={{ fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-muted)' }}>
                    {davkaZapnuta
                        ? 'V téhle fázi žádné kontroly nejsou. Až dostaneš dávku, objeví se tady.'
                        : 'V téhle fázi žádné kontroly nejsou.'}
                </div>
            </div>
            {prouzekPrehled && <div style={{ paddingBottom: 8 }}>{prouzekPrehled}</div>}
            {modalPrehled}
            </>
        );
    }

    const pocetChecklistu = maChecklist ? pocetKontrol(faze) : null;

    return (
        <>
            {/* Dávka je vždycky nahoře — ať je rozbalená, nebo sražená do proužku.
                Pořadí, které se mění podle stavu, by se špatně hledalo palcem. */}
            {maDavku && (aktivni === 'davka' ? (
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                    <DavkaPanel
                        davka={davka} zastavky={zastavky} nadchazejici={nadchazejici}
                        onRefetch={refetch}
                        onOpenCockpit={onOpenCockpit}
                        isDark={isDark}
                    />
                </div>
            ) : (
                <Prouzek
                    Icon={Truck}
                    label={`Dávka ${davka.kod ?? '#' + davka.id}`}
                    doplnek={STAV_DAVKY[davka.stav] || null}
                    barva="#975ACD"
                    onClick={() => setVolba('davka')}
                    isDark={isDark}
                />
            ))}

            {maOdvoz && (aktivni === 'odvoz' ? odvoz : prouzekOdvoz(() => setVolba('odvoz')))}

            {maChecklist && (aktivni === 'checklist' ? (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <ChecklistPanel
                        faze={faze}
                        onSave={onSaveOdpoved}
                        onOpenPhase={onOtevritFazi}
                        isDark={isDark}
                        fill={fill}
                    />
                </div>
            ) : (
                <Prouzek
                    Icon={ListChecks}
                    label={faze.checklisty[0]?.nazev || 'Kontroly'}
                    doplnek={pocetChecklistu}
                    barva="#10B981"
                    onClick={() => setVolba('checklist')}
                    isDark={isDark}
                />
            ))}

            {prouzekPrehled}
            {modalPrehled}
        </>
    );
}

/** „2/5" — kolik položek kontrol aktuální fáze je hotových. */
function pocetKontrol(faze) {
    const done = faze.checklisty.reduce((a, c) => a + (c.done_count || 0), 0);
    const celkem = faze.checklisty.reduce((a, c) => a + (c.total || 0), 0);
    return `${done}/${celkem}`;
}

const STAV_DAVKY = {
    pripraveno: 'k převzetí',
    potvrzena: 'k vyzvednutí',
    predano_ridici: 'převzato',
    na_ceste: 'na cestě',
    uzavreno: 'doručeno',
    v_priprave: 'KDS skládá',
};

/** Sražený segment — jeden řádek na dotyk, ťuknutí ho rozbalí. */
function Prouzek({ Icon, label, doplnek, barva, onClick, isDark }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: 9,
                minHeight: 40,
                margin: '0 12px', width: 'calc(100% - 24px)',
                padding: '0 12px', borderRadius: 10,
                background: isDark ? 'rgba(255,255,255,0.045)' : 'var(--surface-elevated)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'var(--border-default)'}`,
                cursor: 'pointer', font: 'inherit', textAlign: 'left',
                WebkitTapHighlightColor: 'transparent',
            }}>
            <Icon size={14} style={{ color: barva, flexShrink: 0 }} />
            <span style={{
                flex: 1, minWidth: 0,
                fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{label}</span>
            {doplnek && (
                <span style={{
                    fontSize: 11.5, fontWeight: 700, color: barva,
                    fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                }}>{doplnek}</span>
            )}
            <ChevronUp size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        </button>
    );
}
